import React from 'react';
import { Hash, X } from 'lucide-react';

export function Hashtags({ tags, onTagClick, onTagRemove, className = "", showRemove = false }) {
  // Преобразуем tags в массив если это строка
  const tagsArray = Array.isArray(tags) ? tags : (tags ? tags.split(',').map(tag => tag.trim()) : []);
  
  if (!tagsArray || tagsArray.length === 0) {
    return null;
  }

  const handleTagClick = (tag) => {
    if (onTagClick) {
      onTagClick(tag);
    }
  };

  const handleTagRemove = (tag, e) => {
    e.stopPropagation();
    if (onTagRemove) {
      onTagRemove(tag);
    }
  };

  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {tagsArray.map((tag, index) => (
        <span
          key={index}
          onClick={() => handleTagClick(tag)}
          className={`
            inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium
            transition-all duration-200 cursor-pointer
            ${onTagClick 
              ? 'bg-blue-100 text-blue-800 hover:bg-blue-200 hover:text-blue-900' 
              : 'bg-gray-100 text-gray-700'
            }
            ${showRemove ? 'pr-1' : ''}
          `}
        >
          <Hash className="w-3 h-3" />
          {tag}
          {showRemove && (
            <button
              onClick={(e) => handleTagRemove(tag, e)}
              className="ml-1 p-0.5 rounded-full hover:bg-gray-300 transition-colors"
              title="Удалить тег"
            >
              <X className="w-2.5 h-2.5" />
            </button>
          )}
        </span>
      ))}
    </div>
  );
}

// Компонент для ввода хештегов
export function HashtagInput({ value, onChange, placeholder = "Добавьте теги...", className = "" }) {
  const [inputValue, setInputValue] = React.useState('');
  const [tags, setTags] = React.useState(value || []);

  React.useEffect(() => {
    setTags(value || []);
  }, [value]);

  const addTag = (tag) => {
    const cleanTag = tag.trim().replace(/^#/, ''); // Убираем # если есть
    if (cleanTag && !tags.includes(cleanTag)) {
      const newTags = [...tags, cleanTag];
      setTags(newTags);
      onChange(newTags);
    }
  };

  const removeTag = (tagToRemove) => {
    const newTags = tags.filter(tag => tag !== tagToRemove);
    setTags(newTags);
    onChange(newTags);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (inputValue.trim()) {
        addTag(inputValue);
        setInputValue('');
      }
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handlePaste = (e) => {
    const pastedText = e.clipboardData.getData('text');
    const pastedTags = pastedText.split(/[,\s]+/).filter(tag => tag.trim());
    
    if (pastedTags.length > 1) {
      e.preventDefault();
      pastedTags.forEach(tag => addTag(tag));
      setInputValue('');
    }
  };

  return (
    <div className={`space-y-3 ${className}`}>
      <label className="block text-sm font-medium text-gray-700">
        Теги (хештеги)
      </label>
      
      {/* Отображение существующих тегов */}
      {tags.length > 0 && (
        <Hashtags 
          tags={tags} 
          onTagRemove={removeTag}
          showRemove={true}
        />
      )}
      
      {/* Поле ввода */}
      <div className="relative">
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          onPaste={handlePaste}
          placeholder={placeholder}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
        />
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          <Hash className="w-4 h-4 text-gray-400" />
        </div>
      </div>
      
      {/* Подсказки */}
      <div className="text-xs text-gray-500">
        <p>• Нажмите Enter или запятую для добавления тега</p>
        <p>• Можно вставить несколько тегов через запятую</p>
        <p>• Теги автоматически получают префикс #</p>
      </div>
    </div>
  );
}

// Компонент для фильтрации по хештегам
export function HashtagFilter({ tags, selectedTags, onTagSelect, onTagDeselect, className = "" }) {
  // Преобразуем tags в массив если это строка
  const tagsArray = Array.isArray(tags) ? tags : (tags ? tags.split(',').map(tag => tag.trim()) : []);
  
  if (!tagsArray || tagsArray.length === 0) {
    return null;
  }

  const handleTagClick = (tag) => {
    if (selectedTags.includes(tag)) {
      onTagDeselect(tag);
    } else {
      onTagSelect(tag);
    }
  };

  return (
    <div className={`space-y-3 ${className}`}>
      <h4 className="text-sm font-medium text-gray-700">Фильтр по тегам</h4>
      <div className="flex flex-wrap gap-2">
        {tagsArray.map((tag, index) => (
          <button
            key={index}
            onClick={() => handleTagClick(tag)}
            className={`
              inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium
              transition-all duration-200 cursor-pointer
              ${selectedTags.includes(tag)
                ? 'bg-rose-100 text-rose-800 border border-rose-300'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }
            `}
          >
            <Hash className="w-3 h-3" />
            {tag}
          </button>
        ))}
      </div>
    </div>
  );
}
